<ng-container *ngIf="authProcess.user$ | async as user; else showForm">
  <!-- This component will be shown when:
    - we just sent a verification mail (notably after sign up)
    - we arrived from the guard after trying to access a protected route even though we are connected
    - config.enableEmailVerification is undefined, null or true
  -->
  <div
    *ngIf="
      config.enableEmailVerification !== false &&
        ((config.guardProtectedRoutesUntilEmailIsVerified &&
          !user.emailVerified) ||
          (authProcess.emailConfirmationSent && !user.emailVerified));
      else signedInUser
    "
    fxLayout="row"
    fxLayoutAlign="center center"
  >
    <pt-auth-firebaseui-email-confirmation
      [email]="user.email"
      [goBackURL]="goBackURL"
      [messageOnEmailConfirmationSuccess]="messageOnEmailConfirmationSuccess"
      [sendNewVerificationEmailText]="sendNewVerificationEmailText"
      [signOutText]="signOutText"
      [template]="verifyEmailTemplate"
      [verifyEmailConfirmationText]="verifyEmailConfirmationText"
      [verifyEmailGoBackText]="verifyEmailGoBackText"
      [verifyEmailTitleText]="verifyEmailTitleText"
      (signOut)="signOut()"
    />
  </div>

  <ng-template #signedInUser>
    <div
      class="signed-in-container"
      fxLayout="column"
      fxLayoutAlign="center center"
    >
      <img
        *ngIf="user?.photoURL; else noPhoto"
        class="account-circle"
        [src]="user?.photoURL"
      />
      <ng-template #noPhoto>
        <mat-icon class="account-circle">account_circle</mat-icon>
      </ng-template>
      <div class="user-display-name mat-title">{{ user?.displayName }}</div>
      <div class="user-email mat-body-2">{{ user?.email }}</div>
      <div class="actions">
        <mat-progress-bar *ngIf="isLoading" mode="indeterminate" />
        <a
          *ngIf="verifyEmailGoBackText"
          class="go-back-button action-button"
          color="primary"
          mat-stroked-button
          [routerLink]="goBackURL"
          >{{ verifyEmailGoBackText }}</a
        >
        <button
          class="sign-out-button action-button"
          color="warn"
          mat-stroked-button
          (click)="signOut()"
        >
          {{ signOutText }}
        </button>
      </div>
    </div>
  </ng-template>
</ng-container>

<ng-template #showForm>
  <mat-tab-group
    [color]="color"
    [selectedIndex]="tabIndex"
    (selectedTabChange)="onTabChange($event)"
  >
    <!--Sign in tab-->
    <mat-tab [label]="signInTabText">
      <mat-card>
        <mat-card-title>{{ signInCardTitleText }}</mat-card-title>
        <mat-card-content>
          <form
            [@animateStagger]="{ value: '50' }"
            [formGroup]="signInFormGroup"
            (ngSubmit)="signIn()"
          >
            <div fxLayout="column" fxLayoutAlign="center">
              <mat-form-field
                [@animate]="{
                  value: '*',
                  params: { duration: '300ms', y: '100px' }
                }"
                [appearance]="appearance"
              >
                <mat-label>{{ emailText }}</mat-label>
                <input
                  formControlName="email"
                  matInput
                  required
                  autocomplete="username"
                />
                <mat-icon matSuffix [color]="color">email</mat-icon>
                <mat-error *ngIf="signInEmailFormControl.hasError('required')">
                  {{ emailErrorRequiredText }}
                </mat-error>
                <mat-error *ngIf="signInEmailFormControl.hasError('pattern')">
                  {{ emailErrorPatternText }}
                </mat-error>
              </mat-form-field>

              <mat-form-field
                [@animate]="{
                  value: '*',
                  params: { duration: '300ms', y: '100px' }
                }"
                [appearance]="appearance"
              >
                <mat-label>{{ passwordText }}</mat-label>
                <input
                  formControlName="password"
                  autocomplete="current-password"
                  matInput
                  required
                  [maxlength]="max"
                  [minlength]="min"
                  [type]="togglePass?.type"
                />
                <mat-pass-toggle-visibility #togglePass matSuffix />
                <mat-icon matSuffix [color]="color">lock</mat-icon>
                <mat-hint align="end" aria-live="polite">
                  {{ signInFormGroup.value.password.length }} / {{ max }}
                </mat-hint>
                <mat-error
                  *ngIf="sigInPasswordFormControl.hasError('required')"
                >
                  {{ passwordErrorRequiredText }}
                </mat-error>
                <mat-error
                  *ngIf="sigInPasswordFormControl.hasError('minlength')"
                >
                  {{ passwordErrorMinLengthText }}
                </mat-error>
                <mat-error
                  *ngIf="sigInPasswordFormControl.hasError('maxlength')"
                >
                  {{ passwordErrorMaxLengthText }}
                </mat-error>
              </mat-form-field>

              <button
                class="space-top"
                mat-raised-button
                style="margin-top: 20px"
                type="submit"
                [@animate]="{ value: '*', params: { x: '50px' } }"
                [color]="color"
                [disabled]="signInFormGroup.invalid"
              >
                {{ loginButtonText }}
              </button>
            </div>
          </form>

          <div fxLayoutAlign="center">
            <button
              *ngIf="resetPasswordEnabled"
              class="space-top"
              mat-button
              [@animate]="{ value: '*', params: { x: '-50px' } }"
              [color]="color"
              (click)="createForgotPasswordTab()"
            >
              {{ forgotPasswordButtonText }}
            </button>
          </div>
        </mat-card-content>
        <mat-card-footer *ngIf="isLoading">
          <mat-progress-bar
            mode="indeterminate"
            [@animate]="{
              value: '*',
              params: { z: '50px', delay: '50ms', scale: '0.2' }
            }"
          />
        </mat-card-footer>
      </mat-card>
    </mat-tab>

    <!--tab register-->
    <mat-tab *ngIf="registrationEnabled" [label]="registerTabText">
      <mat-card>
        <mat-card-title>{{ registerCardTitleText }}</mat-card-title>
        <mat-card-content fxLayout="column" fxLayoutAlign="center">
          <form
            [@animateStagger]="{ value: '50' }"
            [formGroup]="signUpFormGroup"
            (ngSubmit)="
              signUpFormGroup.valid &&
                processLegalSignUP(authProvider.EmailAndPassword)
            "
          >
            <div fxLayout="column" fxLayoutAlign="center">
              <!--name-->
              <mat-form-field
                [@animate]="{ value: '*', params: { x: '50px' } }"
                [appearance]="appearance"
              >
                <!--labels will work only with @angular/material@6.2.0 -->
                <mat-label>{{ nameText }}</mat-label>
                <input
                  matInput
                  required
                  [formControl]="sigUpNameFormControl"
                  [maxlength]="config.nameMaxLength"
                  [minlength]="config.nameMinLength"
                />
                <mat-icon matSuffix [color]="color">person</mat-icon>
                <mat-hint align="end" aria-live="polite">
                  {{ signUpFormGroup.value.name.length }} /
                  {{ config.nameMaxLength }}
                </mat-hint>
                <mat-error *ngIf="sigUpNameFormControl.hasError('required')">
                  {{ nameErrorRequiredText }}
                </mat-error>
                <mat-error *ngIf="sigUpNameFormControl.hasError('minlength')">
                  {{ nameErrorMinLengthText }}
                </mat-error>
                <mat-error *ngIf="sigUpNameFormControl.hasError('maxlength')">
                  {{ nameErrorMaxLengthText }}
                </mat-error>
              </mat-form-field>

              <!--email-->
              <mat-form-field
                [@animate]="{ value: '*', params: { x: '50px' } }"
                [appearance]="appearance"
              >
                <mat-label>{{ emailText }}</mat-label>
                <input
                  matInput
                  required
                  type="email"
                  autocomplete="username"
                  [formControl]="sigUpEmailFormControl"
                />
                <mat-icon matSuffix [color]="color">email</mat-icon>
                <mat-error *ngIf="sigUpEmailFormControl.hasError('required')">
                  {{ emailErrorRequiredText }}
                </mat-error>
                <mat-error *ngIf="sigUpEmailFormControl.hasError('pattern')">
                  {{ emailErrorPatternText }}
                </mat-error>
              </mat-form-field>

              <!--password-->
              <div fxLayout="column">
                <mat-form-field
                  [@animate]="{ value: '*', params: { x: '50px' } }"
                  [appearance]="appearance"
                >
                  <mat-label>{{ passwordText }}</mat-label>
                  <input
                    matInput
                    name="password"
                    autocomplete="new-password"
                    required
                    [formControl]="sigUpPasswordFormControl"
                    [maxlength]="max"
                    [minlength]="min"
                    [type]="toggle.type"
                  />
                  <mat-pass-toggle-visibility #toggle matSuffix />

                  <mat-icon matSuffix [color]="color">lock</mat-icon>

                  <mat-hint align="end" aria-live="polite">
                    {{ signUpFormGroup.value.password.length }} / {{ max }}
                  </mat-hint>

                  <mat-error
                    *ngIf="sigUpPasswordFormControl.hasError('required')"
                    class="cut-text"
                  >
                    {{ passwordErrorRequiredText }}
                  </mat-error>

                  <mat-error
                    *ngIf="sigUpPasswordFormControl.hasError('minlength')"
                    class="cut-text"
                  >
                    {{ passwordErrorMinLengthText }}
                  </mat-error>
                  <mat-error
                    *ngIf="sigUpPasswordFormControl.hasError('maxlength')"
                    class="cut-text"
                  >
                    {{ passwordErrorMaxLengthText }}
                  </mat-error>
                </mat-form-field>

                <mat-password-strength
                  #passwordStrength
                  [customValidator]="customValidator"
                  [enableDigitRule]="enableDigitRule"
                  [enableLengthRule]="enableLengthRule"
                  [enableLowerCaseLetterRule]="enableLowerCaseLetterRule"
                  [enableSpecialCharRule]="enableSpecialCharRule"
                  [enableUpperCaseLetterRule]="enableUpperCaseLetterRule"
                  [externalError]="sigUpPasswordFormControl.dirty"
                  [max]="max"
                  [min]="min"
                  [password]="signUpFormGroup.value.password"
                />
              </div>

              <button
                mat-raised-button
                style="margin-top: 20px"
                type="submit"
                [@animate]="{ value: '*', params: { x: '100px' } }"
                [color]="color"
                [disabled]="signUpFormGroup.invalid"
              >
                {{ registerButtonText }}
              </button>
            </div>
          </form>

          <button
            *ngIf="guestEnabled"
            mat-button
            style="margin-top: 20px"
            [@animate]="{ value: '*', params: { x: '-100px' } }"
            [color]="color"
            (click)="processLegalSignUP(authProvider.ANONYMOUS)"
          >
            <mat-icon>fingerprint</mat-icon>
            {{ guestButtonText }}
          </button>
        </mat-card-content>

        <mat-card-footer *ngIf="isLoading">
          <mat-progress-bar
            mode="indeterminate"
            [@animate]="{
              value: '*',
              params: { z: '50px', delay: '50ms', scale: '0.2' }
            }"
          />
        </mat-card-footer>
      </mat-card>
    </mat-tab>

    <!--Reset password tab-->
    <mat-tab *ngIf="passwordResetWished" class="reset-password-tab">
      <ng-template mat-tab-label>
        <button
          class="reset-password-tab__close-button"
          mat-icon-button
          (click)="passwordResetWished = false"
        >
          {{ resetPasswordTabText }}
          <mat-icon>close</mat-icon>
        </button>
      </ng-template>
      <form
        [@animateStagger]="{ value: '50' }"
        [formGroup]="resetPasswordFormGroup"
        (ngSubmit)="resetPasswordFormGroup.valid && resetPassword()"
      >
        <mat-card class="reset-password-card">
          <mat-card-content>
            <mat-form-field
              class="full-width"
              [@animate]="{
                value: '*',
                params: { duration: '300ms', y: '100px' }
              }"
              [appearance]="appearance"
            >
              <mat-label> {{ resetPasswordInputText }} </mat-label>
              <input
                formControlName="email"
                matInput
                required
                [title]="resetPasswordInputText"
              />
              <mat-icon matSuffix [color]="color">email</mat-icon>
              <mat-error
                *ngIf="resetPasswordEmailFormControl.hasError('required')"
              >
                {{ resetPasswordErrorRequiredText }}
              </mat-error>
              <mat-error
                *ngIf="resetPasswordEmailFormControl.hasError('pattern')"
              >
                {{ resetPasswordErrorPatternText }}
              </mat-error>
            </mat-form-field>
            <p *ngIf="passReset">{{ resetPasswordInstructionsText }}</p>
          </mat-card-content>
          <mat-card-actions fxLayoutAlign="center">
            <mat-progress-bar *ngIf="isLoading" mode="indeterminate" />
            <button
              mat-raised-button
              type="submit"
              [@animate]="{ value: '*', params: { x: '50px' } }"
              [color]="color"
            >
              {{ resetPasswordActionButtonText }}
            </button>
          </mat-card-actions>
        </mat-card>
      </form>
    </mat-tab>
  </mat-tab-group>
  <mat-divider />
  <pt-auth-providers
    *ngIf="tabIndex !== 2"
    [providers]="providers"
    [theme]="providersTheme"
    [tosUrl]="tosUrl"
    [privacyPolicyUrl]="privacyPolicyUrl"
  />
</ng-template>
